import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IdexService } from 'app/idex.service';
import { Toast } from 'app/shared/toast/toast';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DropboxUploaderService {

   private chunkSize = 1048576;
   busy: boolean;

   constructor(private translate: TranslateService, private service: IdexService, private zone: NgZone, private toast: Toast) { }

   getDuplicateFileId(name:string, duplicateFile:any[]): string{
      return duplicateFile?.find(file => file.name === name) ? duplicateFile.find(file => file.name === name).id : null;
   }

   process(access_token, list: File[], callback: any, paths: string[]): void {
      this.busy = true;
      this.uploadFiles(access_token, list, callback, paths);
   }

   uploadFiles(access_token: any, files: any, callback: any, paths: string[]): void {
      if (files.length === 0) {
         this.busy = false;
         return;
      }
      this.zone.run(() => { callback.init(files); });
      let index = 0;
      const uploadNextFile = () => {
         if (index === files.length) {
            this.busy = false;
            this.zone.run(() => { callback.complete(); });
            return;
         }
         const file: any = files[index];
         let path;
         let tempPath = [];
         if (paths.length === 1) {
            path = paths[0];
         } else {
            path = paths[index];
         }
         tempPath.push(path);
         index++;
          
         if (file.canceled) {
            this.zone.run(() => { callback.cancelUpload(index - 1, 'Cancel'); });
            uploadNextFile();
            return;
         }
         if (/[/\\:*?"<>|]/.test(file.name)) {
            this.translate.get('alert.create_folder_error').subscribe(s=>{
               this.toast.error(s);});
            this.zone.run(() => { callback.specialCharacter(index - 1); });
            uploadNextFile();
            return;
         }
         let end = Math.min(this.chunkSize, file.size);
         let offset = 0;
         let blob = file.slice(offset, end);
         const formData = this.getFormData(access_token,path,blob,file.name);
         this.init(formData).subscribe(result => {
            offset = this.chunkSize;
            end = Math.min(offset + this.chunkSize, file.size);
            blob = file.slice(offset, end);
            const send = () => { 
               if (offset > file.size) {
                  offset = file.size;
               }
               const formData = this.getFormData(access_token,path,blob,file.name,offset,result.sessionId);
               this.service.dropboxChunkedUpload(formData).subscribe(res => {
                  if (offset < file.size) {
                     if (file.canceled) {
                        this.zone.run(() => {
                           callback.cancelUpload(index - 1, 'Cancel');
                        });
                        uploadNextFile();
                        return;
                     }
                     const progress = Math.floor(offset * 100 / file.size);
                     this.zone.run(() => { callback.progress(index - 1, progress); });
                     offset += this.chunkSize;
                     end = Math.min(offset + this.chunkSize, file.size);
                     blob = file.slice(offset, end);
                     send();
                  } else {
                     this.service.dropboxFinishChunkedUpload(formData).subscribe(res => {
                        this.zone.run(() => { callback.progress(index - 1, 100); });
                        uploadNextFile();
                     }, error => {
                        file.error = error;
                        if (error.message.includes('malformed')){
                           this.translate.get('dropbox.malformed_error').subscribe(s => {
                              this.zone.run(() => { callback.cancelUpload(index - 1, s); });
                           })
                        } else {
                           this.zone.run(() => { callback.cancelUpload(index - 1, error); });
                        }
                        uploadNextFile();
                     });
                  }
            }, error => {
               file.error = error;
               this.zone.run(() => { callback.cancelUpload(index - 1, error); });
               uploadNextFile();
            })};
            send();
         }, error => {
            file.error = error;
            this.zone.run(() => { callback.cancelUpload(index - 1, error); });
            uploadNextFile();
         });
      };
      uploadNextFile();
   }

   private getFormData(access_token: string,path: string,blob: any, fileName: string, offset?: number, sessionId?: string): FormData {
      let formData = new FormData();
      let body = {
         token: access_token,
         refreshToken: localStorage.getItem('refresh_token_dropbox'),
         path: path,
      };
      if (sessionId) {
         body['sessionId'] = sessionId;
      }
      if (offset) {
         body['offset'] = offset;
      }
      formData.append('data', new Blob([JSON.stringify(body)], {type: 'application/json'}));
      formData.append('file', blob, fileName);
      return formData;
   }

   private init(formData: FormData): Observable<any> {
      return this.service.dropboxInitChunkedUpload(formData);
   }
}